import type { Ref } from 'vue'
import { orderBy } from 'lodash-es'

export interface TableSortingMetadata {
  tableIndex: number
  columnIndex: number
  direction: 'asc' | 'desc' | 'original'
}

export interface TextComponentMetadata {
  tableSorting?: TableSortingMetadata[]
}

// Store original table rows for each table
const originalTableRows = new Map<string, HTMLTableRowElement[]>()

// Store table click handlers to prevent duplicates
const tableHandlers = new WeakMap<HTMLTableElement, boolean>()

/**
 * Clears the saved original table structure for a specific component
 * This should be called when content changes to ensure the snapshot is up-to-date
 */
export function resetTableStructureSnapshot(componentId: string): void {
  // Remove all snapshots for the component
  const keysToRemove: string[] = []
  originalTableRows.forEach((_, key) => {
    if (key.startsWith(`${componentId}-`)) {
      keysToRemove.push(key)
    }
  })

  keysToRemove.forEach((key) => {
    originalTableRows.delete(key)
  })
}

/**
 * Applies sorting functionality to all tables within a container element
 */
export function setupTableSorting(
  contentElement: HTMLElement | null,
  componentId: string,
  canvasMetadata: Ref<
    (Record<string, any> & { presentation_component_metadata: Record<string, any> }) | null | undefined
  >,
  saveSortCallback: (tableIndex: number, columnIndex: number, direction: 'asc' | 'desc' | 'original') => Promise<void>,
  forceRefreshSnapshot = false,
): void {
  if (!contentElement) {
    return
  }

  // Force refresh the snapshot if requested
  if (forceRefreshSnapshot) {
    resetTableStructureSnapshot(componentId)
  }

  // Get the saved sorting metadata for this component
  const componentMetadata: TextComponentMetadata =
    canvasMetadata?.value?.presentation_component_metadata?.[componentId] || {}
  const savedTableSorting = componentMetadata.tableSorting || []

  // Find all tables in the content
  const tables = contentElement.querySelectorAll('table')

  if (!tables.length) {
    return
  }

  tables.forEach((table, tableIndex) => {
    const rows = table.querySelectorAll('tr')
    if (!rows.length) {
      return
    }

    // Store original table structure if not already stored or if forced refresh
    const tableKey = `${componentId}-${tableIndex}`
    // Clear previous snapshot if force refresh is requested - this ensures we get fresh data
    if (forceRefreshSnapshot && originalTableRows.has(tableKey)) {
      originalTableRows.delete(tableKey)
    }

    if (!originalTableRows.has(tableKey)) {
      const tbody = table.querySelector('tbody') || table
      const allRows = Array.from(tbody.querySelectorAll('tr'))
      const firstRowIsHeader = allRows.length > 0 && allRows[0].querySelector('th') !== null
      const originalRows = firstRowIsHeader ? allRows.slice(1) : allRows

      // Deep clone with all content and attributes preserved
      originalTableRows.set(
        tableKey,
        originalRows.map((row) => {
          const clonedRow = row.cloneNode(true) as HTMLTableRowElement
          return clonedRow
        }),
      )
    }

    // Apply click handlers to table headers (only once per table)
    handleTableSorting(table, tableIndex, componentId, saveSortCallback)

    // Apply saved sorting state if available
    const savedSorting = savedTableSorting.find((item) => item.tableIndex === tableIndex)
    if (savedSorting) {
      applySavedSorting(table, tableIndex, componentId, savedSorting)
    }
  })
}

/**
 * Applies the saved sorting state to a table
 */
function applySavedSorting(
  table: HTMLTableElement,
  tableIndex: number,
  componentId: string,
  savedSorting: TableSortingMetadata,
): void {
  const firstRow = table.querySelector('tr')
  if (!firstRow) return

  const headers = firstRow.querySelectorAll('th')
  const headerCount = headers.length

  if (savedSorting.columnIndex >= headerCount) {
    return
  }

  // Apply the correct sorting state
  if (savedSorting.direction === 'original') {
    restoreOriginalOrder(table, tableIndex, componentId)
  } else {
    const isAscending = savedSorting.direction === 'asc'
    sortTableByColumn(table, savedSorting.columnIndex, isAscending)
  }

  // Update all header visual states
  headers.forEach((header) => {
    header.setAttribute('data-sort-direction', 'original')
    header.classList.remove('sorted-asc', 'sorted-desc', 'sorted-original')
  })

  // Set the active header state
  if (savedSorting.columnIndex < headers.length) {
    const activeHeader = headers[savedSorting.columnIndex]
    activeHeader.setAttribute('data-sort-direction', savedSorting.direction)

    if (savedSorting.direction === 'asc') {
      activeHeader.classList.add('sorted-asc')
    } else if (savedSorting.direction === 'desc') {
      activeHeader.classList.add('sorted-desc')
    } else {
      activeHeader.classList.add('sorted-original')
    }
  }
}

/**
 * Handles table sorting by setting up click handlers on headers
 * Uses a WeakMap to ensure handlers are only applied once per table
 */
function handleTableSorting(
  table: HTMLTableElement,
  tableIndex: number,
  componentId: string,
  saveSortCallback: (tableIndex: number, columnIndex: number, direction: 'asc' | 'desc' | 'original') => Promise<void>,
): void {
  // Check if we've already set up handlers for this table
  if (tableHandlers.has(table)) {
    return
  }

  const firstRow = table.querySelector('tr')
  if (!firstRow) return

  const headers = firstRow.querySelectorAll('th')
  if (!headers.length) return

  // Apply handlers to each header
  headers.forEach((header, colIndex) => {
    // Add styling
    header.classList.add('sortable-header')
    header.style.cursor = 'pointer'
    header.style.position = 'relative'
    header.style.paddingRight = '24px'

    // Set initial direction to 'original'
    if (!header.hasAttribute('data-sort-direction')) {
      header.setAttribute('data-sort-direction', 'original')
    }

    // Set up the click handler
    header.addEventListener('click', function headerClickHandler(event) {
      event.preventDefault()
      event.stopPropagation()

      // Get current direction and determine new direction
      const currentDirection = header.getAttribute('data-sort-direction') || 'original'

      let newDirection: 'asc' | 'desc' | 'original'
      if (currentDirection === 'original') {
        newDirection = 'asc'
      } else if (currentDirection === 'asc') {
        newDirection = 'desc'
      } else {
        newDirection = 'original'
      }

      // Reset all headers
      headers.forEach((h) => {
        h.setAttribute('data-sort-direction', 'original')
        h.classList.remove('sorted-asc', 'sorted-desc', 'sorted-original')
      })

      // Update this header's state
      header.setAttribute('data-sort-direction', newDirection)
      if (newDirection === 'asc') {
        header.classList.add('sorted-asc')
        sortTableByColumn(table, colIndex, true)
      } else if (newDirection === 'desc') {
        header.classList.add('sorted-desc')
        sortTableByColumn(table, colIndex, false)
      } else {
        header.classList.add('sorted-original')
        restoreOriginalOrder(table, tableIndex, componentId)
      }

      // Save the sorting state
      saveSortCallback(tableIndex, colIndex, newDirection)
    })
  })

  // Mark this table as having handlers set up
  tableHandlers.set(table, true)
}

/**
 * Restores the original order of table rows
 */
function restoreOriginalOrder(table: HTMLTableElement, tableIndex: number, componentId: string): void {
  const tableKey = `${componentId}-${tableIndex}`
  const originalRows = originalTableRows.get(tableKey)

  if (!originalRows || originalRows.length === 0) {
    return
  }

  const tbodyElement = table.querySelector('tbody')
  const tbody = tbodyElement || table

  const allRows = Array.from(tbody.querySelectorAll('tr'))
  const firstRowIsHeader = allRows.length > 0 && allRows[0].querySelector('th') !== null

  // Keep the header row if it exists
  if (firstRowIsHeader) {
    // Keep the header row

    // Remove all rows except header
    while (tbody.childNodes.length > 1) {
      tbody.removeChild(tbody.lastChild as Node)
    }

    // Append deep-cloned original rows
    originalRows.forEach((row) => {
      const newRow = row.cloneNode(true) as HTMLTableRowElement
      tbody.appendChild(newRow)
    })
  } else {
    // Remove all existing rows
    while (tbody.firstChild) {
      tbody.removeChild(tbody.firstChild)
    }

    // Append deep-cloned original rows
    originalRows.forEach((row) => {
      const newRow = row.cloneNode(true) as HTMLTableRowElement
      tbody.appendChild(newRow)
    })
  }
}

/**
 * Determines if a value is numeric
 */
function isNumeric(value: string): boolean {
  if (!value) return false

  // Better regex for detecting numbers with currency symbols, percentages, and thousand separators
  // This will match formats like $1,234.56, 1.234,56€, 50%, 1,000, etc.
  const numericRegex = /^[-+]?[$€£¥]?[0-9,.\s]*\.?\d+[%]?$/

  // First check if it matches our pattern
  if (!numericRegex.test(value.trim())) {
    return false
  }

  // Then verify we can actually convert it to a number
  const cleanValue = value.replace(/[$€£¥%,\s]/g, '')
  return !isNaN(parseFloat(cleanValue)) && isFinite(Number(cleanValue))
}

/**
 * Converts a string to a number if possible, otherwise returns lowercase string for case-insensitive sorting
 */
function parseNumericValue(value: string): number | string {
  if (!value) return ''

  const trimmedValue = value.trim()

  if (isNumeric(trimmedValue)) {
    // Better cleaning of numeric values
    // Remove currency symbols, commas, spaces, and percentage signs
    // Handle both period and comma as decimal separators based on format
    let cleanValue = trimmedValue.replace(/[$€£¥\s]/g, '')

    // Check if it's a percentage
    const isPercentage = cleanValue.includes('%')
    if (isPercentage) {
      cleanValue = cleanValue.replace(/%/g, '')
    }

    // Use a more robust approach to handle international number formats
    let numericValue: number

    // If it has both commas and periods, determine which is the decimal separator
    if (cleanValue.includes(',') && cleanValue.includes('.')) {
      // If the last separator is a comma, it's likely European format (1.234,56)
      if (cleanValue.lastIndexOf(',') > cleanValue.lastIndexOf('.')) {
        cleanValue = cleanValue.replace(/\./g, '').replace(',', '.')
      }
      // Otherwise, it's likely US format (1,234.56)
      else {
        cleanValue = cleanValue.replace(/,/g, '')
      }
    }
    // If it only has commas, determine if they're thousand separators or decimal points
    else if (cleanValue.includes(',')) {
      // If it's likely a decimal comma (position from right is <= 3)
      if (cleanValue.length - cleanValue.lastIndexOf(',') <= 3) {
        cleanValue = cleanValue.replace(',', '.')
      } else {
        // It's thousand separators
        cleanValue = cleanValue.replace(/,/g, '')
      }
    }

    numericValue = parseFloat(cleanValue)

    // Apply percentage conversion if needed
    if (isPercentage) {
      numericValue = numericValue / 100
    }

    return numericValue
  }

  // Return lowercase string for case-insensitive sorting
  return trimmedValue.toLowerCase()
}

/**
 * Sorts table rows by the specified column
 */
function sortTableByColumn(table: HTMLTableElement, columnIndex: number, ascending: boolean): void {
  // Explicitly check for tbody, use the table if not found
  const tbodyElement = table.querySelector('tbody')
  const tbody = tbodyElement || table

  const allRows = Array.from(tbody.querySelectorAll('tr'))
  if (!allRows.length) {
    return
  }

  const firstRowIsHeader = allRows.length > 0 && allRows[0].querySelector('th') !== null
  const rows = firstRowIsHeader ? allRows.slice(1) : allRows

  if (rows.length === 0) {
    return
  }

  const getCellValue = (row: HTMLTableRowElement) => {
    const cells = Array.from(row.querySelectorAll('td, th'))
    if (columnIndex >= cells.length) return ''

    const cell = cells[columnIndex]
    if (!cell) return ''

    const value = cell.textContent?.trim() || ''
    return parseNumericValue(value)
  }

  const sortedRows = orderBy(rows, [getCellValue], [ascending ? 'asc' : 'desc'])

  if (firstRowIsHeader) {
    while (tbody.childNodes.length > 1) {
      tbody.removeChild(tbody.lastChild as Node)
    }
  } else {
    while (tbody.firstChild) {
      tbody.removeChild(tbody.firstChild)
    }
  }

  sortedRows.forEach((row) => {
    tbody.appendChild(row)
  })
}
